import React from 'react';
import { createPortal } from 'react-dom';
import FocusedDialog from '../ui/FocusedDialog';
import { Check, Search as SearchIcon, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface ColumnFilterItem<V extends string = string> {
  value: V;
  label: string;
  /** Optional count (e.g. how many rows currently match this value) — shown as a muted badge. */
  count?: number;
  /** Optional left decoration — e.g. the coloured dot for the status filter. */
  leftContent?: React.ReactNode;
  /** Optional tailwind class applied to the label — e.g. a semantic colour for
   *  the Health filter so each row picks up its matching box colour. */
  labelClass?: string;
}

interface ColumnFilterPopoverProps<V extends string = string> {
  items: ColumnFilterItem<V>[];
  selected: ReadonlySet<V>;
  onChange: (next: Set<V>) => void;
  /** Anchor element — the popover positions itself below this. */
  anchor: HTMLElement;
  /** Show the embedded search box. Auto-enabled when there are >7 items. */
  searchable?: boolean;
  /** Align popover's left / right edge with the anchor. Default: auto. */
  align?: 'left' | 'right';
  /** Label rendered above the list — e.g. "Filter by underlying". */
  title?: string;
  /** Accessible label used when `title` is omitted. */
  ariaLabel?: string;
  onClose: () => void;
}

const POPOVER_WIDTH = 240;

function computePosition(
  anchor: HTMLElement,
  align: 'left' | 'right',
): { top: number; left: number } {
  const rect = anchor.getBoundingClientRect();
  const top = rect.bottom + 6; // 6px = mt-1.5
  if (align === 'right') {
    return { top, left: rect.right - POPOVER_WIDTH };
  }
  return { top, left: rect.left };
}

/**
 * Minimal multi-select popover for every header filter on the Schedule
 * table. Renders via React portal to document.body so that nothing — not
 * `overflow-x-auto`, not table cell clipping — can crop it.
 *
 * Styling follows the 2025 minimalist brief: soft shadow, generous spacing,
 * colour only on interaction.
 */
export function ColumnFilterPopover<V extends string = string>({
  items, selected, onChange, anchor, searchable, align, title, ariaLabel, onClose,
}: ColumnFilterPopoverProps<V>) {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [query, setQuery] = React.useState('');
  const [coords, setCoords] = React.useState<{ top: number; left: number }>(() =>
    computePosition(anchor, align ?? 'left'));

  // Pick the alignment that keeps the popover fully inside the viewport if
  // the caller didn't force one. Measured from the anchor's live rect so
  // column-reordering never breaks alignment.
  const resolvedAlign: 'left' | 'right' = React.useMemo(() => {
    if (align) return align;
    const rect = anchor.getBoundingClientRect();
    const SAFETY = 16;
    return rect.left + POPOVER_WIDTH + SAFETY > window.innerWidth ? 'right' : 'left';
  }, [anchor, align]);

  // Reposition continuously while open — glue the popover to the anchor
  // through every layout change: window scroll/resize, ancestor scrolling
  // (the schedule table sits in an `overflow-x-auto` div), content-driven
  // column-width reflow when the user applies a filter, and font-load
  // shifts. We combine three triggers:
  //   1. useLayoutEffect with no deps → re-measures after every React render
  //      (catches state-driven layout changes in the parent table).
  //   2. ResizeObserver on the anchor → catches size changes that don't
  //      come through React (DOM mutations, CSS transitions).
  //   3. window scroll/resize listeners → catches viewport-level movement.
  // Guard against re-render loops: only commit state when the computed
  // coordinates actually changed (coords is a fresh object each call).
  const updateCoords = React.useCallback(() => {
    const next = computePosition(anchor, resolvedAlign);
    setCoords((prev) => (prev.top === next.top && prev.left === next.left ? prev : next));
  }, [anchor, resolvedAlign]);

  // useLayoutEffect with no deps → re-measures after every React render,
  // catching state-driven layout changes in the parent table (e.g. a
  // filter change that shortens the basket-name column and shifts
  // every subsequent header icon).
  React.useLayoutEffect(() => {
    updateCoords();
  });

  React.useEffect(() => {
    const ro = new ResizeObserver(updateCoords);
    ro.observe(anchor);
    // Observe body too — column-width reflow in the table propagates
    // through document layout, and ResizeObserver on the anchor alone
    // doesn't fire when only the anchor's X position shifts.
    ro.observe(document.body);
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [anchor, updateCoords]);

  // Outside-click / Escape dismissal.
  //
  // CAPTURE PHASE is critical. The column-filter trigger button uses
  // `onMouseDown={(e) => e.stopPropagation()}` to prevent the table's
  // column-drag handler from latching onto the click. Without capture, that
  // stopPropagation would also block this document-level listener — meaning
  // clicking a *second* filter button wouldn't dismiss the *first* popover.
  // Capture-phase listeners fire BEFORE the event reaches the target, so
  // they run before stopPropagation can take effect. Result: opening one
  // filter always closes any other open filter, as expected.
  React.useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t)) return;
      if (anchor.contains(t)) return;  // clicks on the trigger toggle via the trigger itself
      onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleKey);
    };
  }, [anchor, onClose]);

  const showSearch = searchable ?? items.length > 7;
  const q = query.trim().toLowerCase();
  const visibleItems = q.length
    ? items.filter((it) => it.label.toLowerCase().includes(q))
    : items;

  const toggle = (v: V) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  const selectAllVisible = () => {
    const next = new Set(selected);
    for (const it of visibleItems) next.add(it.value);
    onChange(next);
  };

  const clearAll = () => onChange(new Set());

  const popover = (
    <FocusedDialog
      innerRef={popoverRef}
      ariaLabel={title ?? ariaLabel ?? 'Column filter'}
      style={{ top: coords.top, left: coords.left, width: POPOVER_WIDTH }}
      className={cn(
        'fixed z-50 rounded-xl border border-gray-200 dark:border-gray-700',
        'bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-xl',
        'animate-in fade-in slide-in-from-top-1 duration-100',
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onDragStart={(e) => e.preventDefault()}
    >
      {title && (
        <div className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}

      {showSearch && (
        <div className="px-2 pt-2 pb-1">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full h-7 pl-7 pr-7 text-xs bg-gray-50 dark:bg-gray-700/60 border border-transparent rounded-md text-gray-700 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      <ul role="listbox" aria-multiselectable="true" className="max-h-60 overflow-y-auto py-1">
        {visibleItems.length === 0 ? (
          <li className="px-3 py-6 text-center text-xs text-gray-400 dark:text-gray-500">
            No matches
          </li>
        ) : (
          visibleItems.map((it) => {
            const isSelected = selected.has(it.value);
            return (
              <li key={it.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggle(it.value)}
                  className={cn(
                    'group w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors',
                    isSelected
                      ? 'text-gray-900 dark:text-white bg-blue-50/70 dark:bg-blue-900/20'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60',
                  )}
                >
                  <span
                    className={cn(
                      'w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center shrink-0 transition-all',
                      isSelected
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'border-gray-300 dark:border-gray-500 group-hover:border-gray-400',
                    )}
                  >
                    {isSelected && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                  </span>
                  {it.leftContent && <span className="shrink-0">{it.leftContent}</span>}
                  <span className={cn('flex-1 truncate font-medium', it.labelClass)}>
                    {it.label}
                  </span>
                  {it.count != null && (
                    <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">
                      {it.count}
                    </span>
                  )}
                </button>
              </li>
            );
          })
        )}
      </ul>

      <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 dark:border-gray-700/80 text-[11px] font-medium">
        <button
          type="button"
          onClick={selectAllVisible}
          disabled={visibleItems.length === 0}
          className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Select {q ? 'matching' : 'all'}
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={selected.size === 0}
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear
        </button>
      </div>
    </FocusedDialog>
  );

  return createPortal(popover, document.body);
}

export default ColumnFilterPopover;
